<template>
    <div v-if="!loading" class="pb-10 xl:max-w-[1440px]">
        <div class="xxs:pl-4 lg:pl-0 flex xxs:flex-col lg:flex-row lg:items-center mb-5 w-full">
            <div class="cursor-pointer w-8 h-8 rounded-full shadow-[0_10px_20px_0px_rgba(0,0,0,0.1)] bg-white flex items-center justify-center mr-10 flex-shrink-0" @click="router.push('/news')">
                <ArrowLeft :color="color.hex" />
            </div>
            <div class="xxs:mt-4 lg:mt-0 inline-flex items-center font-body-6 text-default-2 overflow-hidden">
                <span @click="router.push('/news')" class="hover:cursor-pointer">News</span>
                <ChevronRight color="#74788D" class="ml-4 mr-4" />
                <h1 class="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{{article.title}}</h1>
            </div>
        </div>
        <div class="bg-white rounded border border-white mt-4 box-border relative rounded-t-[4px]">
            <Image v-if="hasCover" :src="cover" type="cover" :lazy-loading="false" no-delay />
            <div class="w-full xxs:p-4 lg:px-6 lg:pb-6" :class="[hasCover > 0 ? 'lg:mt-8' : '']">
                <div class="w-full h-6 flex items-center justify-between">
                    <div v-if="isScheduled && !isPastDate" class="text-body-6 text-interactive-6 flex">
                        <img src="@/assets/icons/schedule.svg" alt="Schedule" class="w-4 mr-2" />
                        <span class="text-body-7 text-default-4">Scheduled on {{formattedDate}}</span>
                    </div>
                    <div v-else class="text-body-6 text-interactive-6">
                        {{formattedDate}}
                    </div>
                    <div class="flex" v-if="article.organization === organization.slug">
                        <div class="flex cursor-pointer" @click="showShareModal = true">
                            <img src="@/assets/icons/share-dark-grey.svg" alt="Share" class="w-4" />
                            <span class="text-body-6 text-interactive-6 ml-2">Share</span>
                        </div>
                        <ActionMenuNewsCard
                            class="ml-4"
                            is-in-article
                            :is-on-current-organization="article.organization === organization.slug"
                            @edit="editArticle"
                            @share="showShareModal = true"
                            @delete="handleDelete"/>
                    </div>
                </div>
                <h1 class="xxs:text-body-2 xxs:mt-6 lg:mt-4 lg:text-display-5 text-default-1">{{article.title}}</h1>
                <div class="w-full mt-4 flex items-center justify-center">
                    <div class="flex-1 flex flex-wrap">
                        <div class="w-full flex mt-4" v-if="hasTags">
                            <NewsTag :tags="article.tags"/>
                        </div>
                    </div>
                </div>
                <div v-html="article.body" class="mt-8 article-body prose">
                </div>
                <div class="flex w-full lg:justify-between items-center mt-4">
                    <Like
                        :likes-count="article.total_likes"
                        :is-liked="article.is_liked"
                        :articleUid="article.uid"
                        @toggle-like="toggleLike"
                    />
                </div>
                <div class="flex w-full mt-12 flex-col" v-if="recommendedArticles.length > 0">
                    <h2 class="xxs:text-body-2 lg:text-body-1 text-default-1">You might also be interested in</h2>
                    <div class="flex w-full flex-wrap">
                        <NewsCard
                            v-for="(item, index) in recommendedArticles"
                            :index="index"
                            :article="item"
                            :key="item.uid"
                            @like="likeRecommendedArticle"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="w-full flex flex-col box-border" v-else>
        <div class="skeleton-loader h-[30px] w-1/2"></div>
        <div class="bg-white rounded border border-white mt-4 box-border relative">
            <div class="skeleton-loader w-full pt-[50%]"></div>
            <div class="w-full xxs:p-4 lg:px-6 lg:pb-6">
                <div class="skeleton-loader w-full h-[56px] mt-6"></div>
                <div class="w-full mt-4 flex">
                    <div class="skeleton-loader w-1/2 h-[20px]"></div>
                    <div class="skeleton-loader w-[calc(50%-16px)] ml-4 h-[20px]"></div>
                </div>
                <div class="skeleton-loader w-full h-[1000px] mt-4"></div>
            </div>
        </div>
    </div>
    <Modal :show="showShareModal" @close="showShareModal = false" class="overflow-visible" :overflow-hidden="false">
        <Share v-model="showShareModal"
               type="news"
               :groups="article.groups"
               :members="article.members"
               :members-count="article.members_count"
               :groups-count="article.groups_count"
               :is-public-shared="article.public"
               :is-global-shared="article.global"
               @share="handleShare"
        />
    </Modal>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {useTheme} from "@/stores/ThemeStore";
import Like from "@/components/custom/news/Like.vue";
import NewsCard from "@/components/cards/NewsCard.vue";
import ActionMenuNewsCard from "@/components/custom/news/ActionMenuNewsCard.vue";
import {useNews} from "@/composables/useNews";
import {toast} from "vue3-toastify";
import {useRoute} from "vue-router";
import {useAuth} from "@/stores/AuthStore";
import {storeToRefs} from "pinia";
import ChevronRight from "@/components/icons/ChevronRight.vue";
import {useRouter} from "vue-router";
import Modal from "@/components/core/Modal.vue";
import Share from "@/components/modals/partials/Share.vue";
import ArrowLeft from "@/components/icons/navigation/ArrowLeft.vue";
import NewsTag from "@/components/custom/news/NewsTag.vue";
import Image from "@/components/custom/Image.vue";
import {useFormatDate} from "@/composables/useFormatDate";

const router = useRouter();
const route = useRoute();

const {organization} = storeToRefs(useAuth());
const {getArticle, deleteArticle, likeArticle, unlikeArticle, shareArticle} = useNews();
const {color} = useTheme();

const loading = ref(true);

const article = ref(null)
const recommendedArticles = ref([]);
const showShareModal = ref(false);

const hasTags = computed(() => {
    return article.value.tags.length > 0;
})

function toggleLike() {
    if (article.value.is_liked) {
        unlikeArticle(organization.value.slug, article.value.uid)
            .then(() => {
                article.value.is_liked = false;
                article.value.total_likes--;
            })
            .catch((error) => {
                console.error(error);
            })
    } else {
        likeArticle(organization.value.slug, article.value.uid)
            .then(() => {
                article.value.is_liked = true;
                article.value.total_likes++;
            })
            .catch((error) => {
                console.error(error);
            })
    }
}

function likeRecommendedArticle(index) {
    recommendedArticles.value[index].liked_by_authenticated_member = !recommendedArticles.value[index].liked_by_authenticated_member;
    recommendedArticles.value[index].likes_count += recommendedArticles.value[index].liked_by_authenticated_member ? 1 : -1;
}

function editArticle() {
    router.push({
        name: 'edit-news',
        params: {
            uid: article.value.uid,
        }
    })
}

function handleDelete() {
    deleteArticle(organization.value.slug, article.value.uid)
        .then(() => {
            toast.success("The news was deleted successfully.");
            router.push({name: 'news'});
        })
        .catch((error) => {
            console.log(error);
            toast.error("There was an error trying to delete the news.");
        })
}

const isPastDate = computed(() => {
    const dateToCheck = new Date(article.value.published_at);

    const currentDate = new Date();

    return dateToCheck < currentDate;
});

const isScheduled = computed(() => {
    if(!article.value) {
        return false;
    }

    return article.value.published_at !== null
})

const hasCover = computed(() => {
    if(!article.value) {
        return false;
    }

    return article.value.cover && article.value.cover.length > 0;
})


const cover = computed(() => {
    if(!article.value || article.value.cover.length === 0) {
        return '';
    }

    return article.value.cover;
})

const formattedDate = computed(() => {
    if(!article.value) {
        return '';
    }

    let date = article.value.created_at;
    if(isScheduled.value) {
        date = article.value.published_at;
    }

    return useFormatDate(date);
});

async function handleShare(data) {
    try {
        if(data.global && !article.value.tags.includes('Global News')) {
            article.value.tags.push('Global News');
        }
        if(!data.global && article.value.tags.includes('Global News')) {
            article.value.tags = article.value.tags.filter(tag => tag !== 'Global News');
        }
        data.tags = article.value.tags;
        const response = await shareArticle(organization.value.slug, article.value.uid , data);
        toast.success("The article was shared successfully.");

        article.value = {
            ...article.value,
            ...response
        }

    } catch (e) {
        console.log(e);
        toast.error("There was an error trying to share the article.");
    } finally {
        showShareModal.value = false;
    }
}

async function getCurrentArticle() {
    getArticle(organization.value.slug, route.params.uid)
        .then(async (response) => {
            article.value = response;
        })
        .catch((error) => {
            console.log(error);
            toast.error('Something went wrong. Please try again.');
        })
        .finally(() => {
            loading.value = false;
        })
}

onMounted(() => {
    getCurrentArticle();
})
</script>

<style lang="scss" scoped>
.article-body {
    iframe{
        @media(max-width: 768px){
            max-width: 100%;
        }
    }
}

ol, ul {
  list-style: disc;
  margin-block-start: 1em;
  margin-block-end: 1em;
  padding-inline-start: 40px;
}
</style>
